import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// Value Content interface
export interface ValueContent {
    _id: string;
    headline: string;
    content: string;
    value_type: string;
    applicable_for: string[];
    end_date?: string;
    is_active: boolean;
    dealership_id: string;
    label?: string; // Short text label for Mia Chat conversation options (<40 characters)
    createdAt?: string;
    updatedAt?: string;
}

interface ValueContentPayload extends Partial<ValueContent> {
    headline: string; // Required field for creating value content
    content: string; // Required field for creating value content
    value_type: ValueContent['value_type']; // Required field for creating value content
    is_active: boolean;
    end_date?: string;
    dealership_id: string;
    applicable_for: string[];
}

interface GetAllValueContentParams {
    skip?: number;
    limit?: number;
    search?: string;
    sort?: string; // MUI DataGrid sort format JSON string
    status?: string;
    filters?: string; // MUI DataGrid filter format JSON string
    dealer?: string; // Add dealership_id for filtering
    type?: string; // Filter by content type
}

class ValueContentService {
    static ENDPOINT = 'api/v2/dealer-value';

    static async getValueContent(contentId: string): Promise<APIResponseType> {
        if (!contentId) throw new Error("Value Content ID is required to fetch details");
        try {
            const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}/${contentId}`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async getAllValueContent(params?: GetAllValueContentParams): Promise<APIResponseType> {
        try {
            const queryParams = new URLSearchParams();

            if (params) {
                if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
                if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
                if (params.search) queryParams.append('search', params.search);
                if (params.sort) queryParams.append('sort', params.sort);
                if (params.status) queryParams.append('status', params.status);
                if (params.filters) queryParams.append('filters', params.filters);
                if (params.dealer) queryParams.append('dealer', params.dealer);
                if (params.type) queryParams.append('type', params.type);
            }

            const queryString = queryParams.toString();
            const url = queryString ? `${this.ENDPOINT}?${queryString}` : this.ENDPOINT;

            const response = await apiService.get<APIResponseType>(url);
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async addValueContent(payload: ValueContentPayload): Promise<APIResponseType> {
        try {
            const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            console.error("API Service - addValueContent error:", error);
            throw error;
        }
    }

    static async deleteValueContent(contentId: string): Promise<APIResponseType> {
        if (!contentId) throw new Error("Value Content ID is required for deletion");
        try {
            const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${contentId}`);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async editValueContent(contentId: string, payload: Partial<ValueContentPayload>): Promise<APIResponseType> {
        if (!contentId) throw new Error("Value Content ID is required for editing");
        if (!Object.keys(payload).length) throw new Error("Edit payload cannot be empty");

        try {
            const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${contentId}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }
}

export default ValueContentService; 